// EmailVerification.tsx - Email verification component
import { useState, useEffect } from 'react';
import {
  Paper,
  Stack,
  Button,
  Text,
  Title,
  Alert,
  Group,
  Center,
  Loader,
} from '@mantine/core';
import {
  IconMail,
  IconCheck,
  IconAlertCircle,
  IconRefresh,
  IconArrowRight,
} from '@tabler/icons-react';
import { useNavigate, useSearchParams } from 'react-router-dom';

export function EmailVerification() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();

  const [status, setStatus] = useState<'loading' | 'success' | 'error' | 'verifying'>('loading');
  const [error, setError] = useState<string | null>(null);
  const [resending, setResending] = useState(false);

  useEffect(() => {
    if (token) {
      verifyEmail(token);
    } else {
      setStatus('loading');
    }
  }, [token]);

  const verifyEmail = async (verifyToken: string) => {
    setStatus('verifying');
    setError(null);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL || 'https://carleads-api-staging.carleads.workers.dev'}/api/auth/verify-email?token=${verifyToken}`
      );

      const data = await response.json();

      if (data.success) {
        setStatus('success');
      } else {
        setStatus('error');
        setError(data.error || 'Verification failed');
      }
    } catch (err: any) {
      setStatus('error');
      setError(err.message || 'Failed to verify email');
    }
  };

  const handleResend = async () => {
    setResending(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Please log in to resend verification email');
        setResending(false);
        return;
      }

      const response = await fetch(
        `${import.meta.env.VITE_API_URL || 'https://carleads-api-staging.carleads.workers.dev'}/api/auth/resend-verification`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      const data = await response.json();

      if (data.success) {
        setStatus('success');
      } else {
        setError(data.error || 'Failed to resend verification');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to resend verification');
    } finally {
      setResending(false);
    }
  };

  if (status === 'loading') {
    return (
      <Center style={{ minHeight: '100vh' }}>
        <Paper p="xl" radius="md" withBorder style={{ maxWidth: 400, width: '100%' }}>
          <Stack align="center" gap="md">
            <Loader size="lg" />
            <Text ta="center" c="dimmed">
              Loading verification...
            </Text>
          </Stack>
        </Paper>
      </Center>
    );
  }

  if (status === 'verifying') {
    return (
      <Center style={{ minHeight: '100vh' }}>
        <Paper p="xl" radius="md" withBorder style={{ maxWidth: 400, width: '100%' }}>
          <Stack align="center" gap="md">
            <Loader size="lg" />
            <Text ta="center" c="dimmed">
              Verifying your email...
            </Text>
          </Stack>
        </Paper>
      </Center>
    );
  }

  if (status === 'success') {
    return (
      <Center style={{ minHeight: '100vh' }}>
        <Paper p="xl" radius="md" withBorder style={{ maxWidth: 400, width: '100%' }}>
          <Stack align="center" gap="md">
            <IconCheck size={64} color="green" />
            <Title order={2} ta="center">
              Email Verified!
            </Title>
            <Text ta="center" c="dimmed">
              Your email has been successfully verified. You can now use all features of the platform.
            </Text>
            <Button
              fullWidth
              leftSection={<IconArrowRight size={16} />}
              onClick={() => navigate('/dashboard')}
            >
              Go to Dashboard
            </Button>
          </Stack>
        </Paper>
      </Center>
    );
  }

  // Error state
  return (
    <Center style={{ minHeight: '100vh' }}>
      <Paper p="xl" radius="md" withBorder style={{ maxWidth: 400, width: '100%' }}>
        <Stack gap="md">
          <Stack align="center" gap="md">
            <IconAlertCircle size={64} color="red" />
            <Title order={2} ta="center">
              Verification Failed
            </Title>
            {error && (
              <Alert icon={<IconAlertCircle size={16} />} color="red" title="Error" style={{ width: '100%' }}>
                {error}
              </Alert>
            )}
            <Text ta="center" c="dimmed">
              The verification link may have expired or is invalid. Please request a new verification email.
            </Text>
          </Stack>

          <Group gap="md">
            <Button
              variant="outline"
              fullWidth
              leftSection={<IconMail size={16} />}
              onClick={handleResend}
              loading={resending}
            >
              Resend Verification
            </Button>
            <Button
              fullWidth
              leftSection={<IconArrowRight size={16} />}
              onClick={() => navigate('/dashboard')}
            >
              Go to Dashboard
            </Button>
          </Group>
        </Stack>
      </Paper>
    </Center>
  );
}
